import { reactive } from 'vue'
import store from './store'
import moment from 'moment'
import { waitIfKey } from '../helperMethods'
import { timedLogger } from '@/helpers/log'
import { getChat, setChat, setStat } from './chats'
import { importTable, deleteKeyFromDB, deleteFromDB } from './database'
import { compareDates } from '@/helpers/utils'
const usersById = reactive({})
const membershipsById = reactive({})
const membershipsByBubbleId = reactive({})
const membershipIdsByUserIdByBubbleId = reactive({})
const organizations = reactive({})

// Adding this here just so it can be available in the Vue dev tools
export const membershipsData = {
	usersById,
	membershipsById,
	membershipsByBubbleId,
	membershipIdsByUserIdByBubbleId,
	organizations,
}

export const sortMembershipsByMarkUpdated = (m1, m2) => {
	return m1.markupdated < m2.markupdated ? -1 : 1
}

export const sortUsersByFullname = (u1, u2) => {
	return u1.fullname.localeCompare(u2.fullname)
}

export const normalizeMembership = (membership) => {
	if (!membership) return null
	let { user, chat, stats, ...rest } = membership
	setChat(chat)
	setStat(stats)
	setUser(user)
	const chatId = rest.chat_id || rest.bubble_id

	rest.muted = rest.muted || rest.mute || false
	rest.mute = rest.muted // TODO: remove this once migration is complete
	rest.muted_expires_at = rest.muted_expires_at || rest.muteuntil || null
	rest.muteuntil = rest.muted_expires_at // TODO: remove this once migration is complete
	rest.meetings_notifications_enabled =
		rest.meetings_notifications_enabled || rest.meetings || false
	rest.meetings = rest.meetings_notifications_enabled // TODO: remove this once migration is complete
	rest.notification_preference =
		rest.notification_preference || rest.notificationpreference || 'ALL'
	rest.notificationpreference = rest.notification_preference // TODO: remove this once migration is complete
	rest.pinned = rest.pinned || rest.is_pinned || false
	rest.is_pinned = rest.pinned // TODO: remove this once migration is complete
	rest.hidden = rest.hidden || rest.ishidden || false
	rest.ishidden = rest.hidden // TODO: remove this once migration is complete

	return {
		...rest,
		userId: rest.user_id,
		chatId,
		bubble_id: chatId, // TODO: remove this once migration is complete
	}
}

const isOldMembership = (membership) => {
	if (!membership) return null
	const previous = getMembership(membership.id)
	if (previous && membership.updated_at && previous.updated_at) {
		const isNewDateNewer =
			compareDates(membership.updated_at, previous.updated_at) > 0
		return isNewDateNewer
	}
	return false
}

const isOldUser = (user) => {
	if (!user) return null
	const previous = getUser(user.id)
	if (previous && user.updated_at && previous.updated_at) {
		const isNewDateNewer =
			compareDates(user.updated_at, previous.updated_at) > 0
		return isNewDateNewer
	}
	return false
}

export const setMembership = (m, skipDB) => {
	if (!m) return null
	if (isOldMembership(m)) {
		console.debug('old membership', m.id, getChat(m.chat_id).displayname)
		// if m is old, get previous (newer) version and use that
		m = getMembership(m.id)
	} else {
		m = normalizeMembership(m)
		membershipsById[m.id] = m
	}
	if (m.chatId) {
		membershipsByBubbleId[m.chatId] = {
			...(membershipsByBubbleId[m.chatId] || {}),
			[m.id]: m.id,
		}
		if (m.user_id) {
			membershipIdsByUserIdByBubbleId[m.user_id] = {
				...(membershipIdsByUserIdByBubbleId[m.user_id] || {}),
				[m.chatId]: m.id,
			}
		}
	}

	if (m.user_id === getMyUserId()) {
		mergeChatPropsFromMembership(m)
	}

	if (!skipDB) {
		store.dbPut('memberships', m)
	}
	EventBus.$emitDebounced('membership-update')
	EventBus.$emitDebounced('chat-update')

	return m
}

export const setMemberships = (memberships, skipDB) => {
	if (!memberships || !memberships.length) return null
	const chatId = memberships[0].chat_id || memberships[0].bubble_id
	if (membershipsByBubbleId[chatId]) {
		membershipsByBubbleId[chatId] = {}
	}
	const normalized = memberships
		.map((m) => setMembership(m, true))
		.filter((m) => m)
	if (normalized.length && !skipDB) {
		store.dbBulkPut('memberships', normalized)
	}
	EventBus.$emitDebounced('membership-update')
	return normalized
}

// Cross-chat-safe variant of setMemberships. Upserts without wiping any chat's
// existing memberships (so other members' `mark` values aren't lost). Use this
// when the array may contain memberships from multiple chats, e.g. when
// aggregating the current user's memberships across their chat list.
export const setMembershipsSafe = (memberships) => {
	if (!memberships || !memberships.length) return null
	const normalized = memberships
		.map((m) => setMembership(m, true))
		.filter((m) => m)
	if (normalized.length) store.dbBulkPut('memberships', normalized)
	return normalized
}

export const mergeChatPropsFromMembership = (m) => {
	m = normalizeMembership(m)
	const chat = getChat(m.chatId)
	if (!chat?.id) return
	Object.assign(chat, {
		is_pinned: m.is_pinned ?? false,
		displayname: m.alias ? m.alias : chat.title,
		role: m.role,
		ishidden: m.ishidden,
		mute: m.mute,
		marked_unread: m.marked_unread || false,
		marked_unread_updated_at: m.marked_unread_updated_at || null,
	})
	setChat(chat, true)
}

export const getMembershipsForBubble = (bubbleId) => {
	return Object.values(membershipsByBubbleId[bubbleId] || {})
		.map(getMembership)
		.filter((m) => m)
		.sort(sortMembershipsByMarkUpdated)
}

export const updateMembershipForUser = (userId, data = {}, bubbleId) => {
	const membership = bubbleId
		? getMembershipForUserAndBubble(userId, bubbleId)
		: getMembershipForUser(userId)
	if (!membership) return null
	updateMembership(membership.id, data)
}

export const updateMembership = (id, m = {}) => {
	const membership = getMembership(id) || {}
	setMembership({ ...membership, ...m })
}

export const normalizeUser = (u) => {
	if (!u) return null
	if (u.organizations?.length) {
		setOrganization(u.organizations[0])
	}
	const isSupportUser = u.role === 'support'
	const isAdmin = isSupportUser || u.role === 'admin'
	const isManager = isSupportUser || isAdmin || u.role === 'manager'

	// Always use profile_pic_url as source of truth, even if null
	if ('profile_pic_url' in u) {
		u.profilepicurl = u.profile_pic_url
	} else if (u.profilepicurl) {
		u.profile_pic_url = u.profilepicurl
	}

	// Now use the synchronized value for both properties
	const profile_pic_url =
		u.profile_pic_url && !isSupportUser
			? store.userProfile(u)
			: u.profile_pic_url

	u.last_presence_at = u.last_presence_at || u.lastpresencetime
	u.lastpresencetime = u.last_presence_at // TODO: remove this once migration is complete
	u.active = !!(u.active || !u.deactivated_at)
	u.inactive = !!(u.inactive || u.deactivated_at)

	if (u.blocked?.length && u.blocked[0]?.id) {
		u.blocked = u.blocked.map((u) => setUser(u)?.id).filter(Boolean)
	}

	// Auto-hide preferences for chats
	// null = never auto-hide, values are in minutes
	const auto_hide_group_mins = u.auto_hide_group_mins ?? null
	const auto_hide_dm_mins = u.auto_hide_dm_mins ?? null
	const auto_hide_thread_mins = u.auto_hide_thread_mins ?? null

	return {
		...u,
		isSupportUser,
		isAdmin,
		isManager,
		fullname:
			u.full_name ||
			u.fullname ||
			[u.first_name || u.firstname, u.last_name || u.lastname]
				.filter(Boolean)
				.join(' '),
		profile_pic_url,
		profilepicurl: profile_pic_url,
		// `profilepic` is the source-of-truth "has a picture" flag. Respect an
		// explicit incoming value; otherwise derive it from the (synchronized)
		// url so it can never disagree with profilepicurl.
		profilepic:
			typeof u.profilepic !== 'undefined' ? !!u.profilepic : !!profile_pic_url,
		// Auto-hide preferences
		auto_hide_group_mins,
		auto_hide_dm_mins,
		auto_hide_thread_mins,
	}
}

// TODO: move all user-related getters/setters out to its own users.js file
export const setUser = (user, skipDB) => {
	if (!user || isOldUser(user)) return null
	if (user.id === getMyUserId()) {
		// our user obj should persist all properties
		user = { ...getMyUser(), ...user }
	}
	user = normalizeUser(user)

	const prev = getUser(user.id)
	if (prev && compareDates(user.lastpresencetime, prev.lastpresencetime) > 0) {
		// always persist the latest presence data
		user.isonline = prev.isonline
		user.lastpresencetime = prev.lastpresencetime
	}

	usersById[user.id] = user
	if (!skipDB) {
		store.dbPut('users', user)
		EventBus.$emitDebounced('user-update', user.id)
		if (user.id === store.auth.user?.id) {
			EventBus.$emitDebounced('my-user-update', user)
		}
		if (!prev) {
			EventBus.$emitDebounced('unknownUserAdded')
		}
	}
	return user
}

export const setUsers = (users) => {
	if (!users) return null
	const normal = users.map((u) => setUser(u, true)).filter((u) => u)
	if (normal.length) {
		store.dbBulkPut('users', normal)
		EventBus.$emitDebounced('user-update')
	}
	return normal
}

export const updateUser = (userId, data = {}) => {
	const user = getUser(userId) || { id: userId }
	return setUser({ ...user, ...data })
}

export const updateUserPresence = (userId, data = {}) => {
	const user = getUser(userId)
	if (!user) return null
	const { isonline = false, lastpresencetime = null } = data
	// only update presence status if timestamp is newer than cache
	if (compareDates(lastpresencetime, user.lastpresencetime) > 0) return null
	return updateUser(userId, { isonline, last_presence_at: lastpresencetime })
}

export const normalizeOrganization = (org) => {
	if (!org) return null
	const copy = Object.assign({}, org)
	copy.grants = copy.grants || {}
	copy.permissions = copy.permissions || {}
	if (!copy.permissions.DEPRECATED) {
		// if not deprecated, copy nested permission to root level for backwards compatibility
		Object.entries(copy.permissions).forEach(([key, value]) => {
			copy[key] = value // Use the value from permissions
		})
		// now copy nested grants to root level for backwards compatibility
		Object.entries(copy.grants).forEach(([key, value]) => {
			key = `grant_${key}`
			copy[key] = value // Use the value from grants
		})
	}
	return copy
}

export const setOrganization = (org) => {
	const normalized = normalizeOrganization(org)
	organizations[0] = normalized
	if (store.auth.user) {
		store.auth.user.organizations[0] = normalized
	}
	EventBus.$emitDebounced('user-update')
	EventBus.$emitDebounced('org-update')
}

export const getOrganization = () => {
	return organizations[0] || null
}

export const updateOrganization = (o = {}) => {
	const org = getOrganization() || {}
	setOrganization({ ...org, ...o })
	EventBus.$emitDebounced('user-update')
	EventBus.$emitDebounced('org-update')
}

export const getMyUserId = () => {
	return store.auth?.user?.id ?? null
}

export const getUser = (userId) => {
	return usersById[userId] || null
}

export const getMyUser = () => {
	return getUser(getMyUserId())
}

export const getUsers = () => {
	return getUsersForBubble(store.currentChatId)
}

export const getUsersForBubble = (bubbleId) => {
	return getMembershipsForBubble(bubbleId)
		.map((m) => getUser(m.user_id))
		.filter((u) => u)
		.sort(sortUsersByFullname)
}

export const getMembershipsWithUsersForBubble = (bubbleId) => {
	return getMembershipsForBubble(bubbleId).map((m) => {
		m.user = getUser(m.user_id)
		return m
	})
}

export const getMembership = (membershipId) => {
	if (!membershipId) return null
	return membershipsById[membershipId]
}

export const getMemberships = () => {
	return getMembershipsForBubble(store.currentChatId)
}

export const getMembershipForUser = (userId) => {
	return getMembershipForUserAndBubble(userId, store.currentChatId)
}

export const getMyMembership = () => {
	return getMembershipForUser(getMyUserId())
}

export const getMembershipForUserAndBubble = (userId, bubbleId) => {
	if (!userId || !bubbleId) return null
	const idsForUser = membershipIdsByUserIdByBubbleId[userId]
	if (!idsForUser) return null
	return getMembership(idsForUser[bubbleId])
}

export const getMyMembershipForBubble = (bubbleId) => {
	return getMembershipForUserAndBubble(getMyUserId(), bubbleId)
}

export const getMembershipsWithUsers = () => {
	return getMembershipsWithUsersForBubble(store.currentChatId)
}

export const getSortedUsersByIds = (userIds = []) => {
	return userIds
		.map(getUser)
		.filter((u) => u)
		.sort(sortUsersByFullname)
}

export const isMyUser = (userId) => {
	return userId === getMyUserId()
}

export const deleteMembership = (id) => {
	const membership = getMembership(id)
	if (!membership) return null
	delete membershipsById[id]
	deleteKeyFromDB('memberships', id)
	if (membershipsByBubbleId[membership.chatId]) {
		delete membershipsByBubbleId[membership.chatId][id]
	}
	EventBus.$emitDebounced('membership-update')
}

export const deleteMembershipsFromCacheForBubble = async (bubble_id) => {
	try {
		return await deleteFromDB('memberships', { bubble_id })
	} catch (e) {
		console.error('cannot delete memberships', bubble_id, e)
	}
}

export const importMyMemberships = async () => {
	const log = timedLogger('my memberships')
	const user_id = getMyUserId()
	if (!user_id) {
		log('no user id, so bailing')
		return
	}
	// leaving this logging in here at debug level so we can diagnose
	//  any future issues that might arise with slow db performance
	log('starting import')
	try {
		const memberships = await importTable('memberships', { where: { user_id } })
		await waitIfKey('slowDB', 8000)
		log('query complete')
		// Loop with skipDB=true to avoid re-persisting what we just read from
		// IndexedDB. setMemberships would wipe the first chat's cache, and
		// setMembershipsSafe always writes back — neither fits here.
		memberships.forEach((m) => setMembership(m, true))
		log('store updated')
	} catch (error) {
		log('error')
		console.error('Cannot decrypt memberships', error)
	}
	log.print()
}

export const importAllUsers = async () => {
	try {
		setUsers(await importTable('users'))
	} catch (e) {
		console.error(e)
	}
}

export const diffMembershipsForBubble = async (memberships) => {
	if (!memberships || !memberships.length) return
	// immediately update the store with the latest memberships
	const normalized = setMemberships(memberships, true)
	// now delete all cached memberships for the bubble from database
	// NOTE: this is slow so we want to do it AFTER updating store
	const chatId = memberships[0].chat_id || memberships[0].bubble_id
	await deleteMembershipsFromCacheForBubble(chatId)
	// now update the database with these new memberships
	store.dbBulkPut('memberships', normalized)
}

export const getClientMeta = (propertyId = null) => {
	const { client_meta = {} } = getMyUser() || {}
	if (propertyId) {
		return client_meta[propertyId] !== undefined
			? client_meta[propertyId]
			: null
	} else {
		return client_meta
	}
}

// Auto-hide preference helpers

/**
 * Get the auto-hide preference for the current user based on chat type
 * @param {string} chatType - 'group', 'dm', or 'thread'
 * @returns {number|null} Minutes until auto-hide, or null if never
 */
export const getAutoHidePreference = (chatType) => {
	const user = getMyUser()
	if (!user) return null

	switch (chatType) {
		case 'group':
			return user.auto_hide_group_mins
		case 'dm':
			return user.auto_hide_dm_mins
		case 'thread':
			return user.auto_hide_thread_mins
		default:
			return null
	}
}

/**
 * Get auto-hide preference for a specific chat
 * Determines the chat type and returns the appropriate preference
 * @param {Object} chat - Chat object
 * @returns {number|null} Minutes until auto-hide, or null if never
 */
export const getAutoHidePreferenceForChat = (chat) => {
	if (!chat) return null

	if (chat.is_thread) {
		return getAutoHidePreference('thread')
	} else if (chat.isDM || chat.is_dm || chat.isdm) {
		return getAutoHidePreference('dm')
	} else {
		return getAutoHidePreference('group')
	}
}

/**
 * Get the effective notification preference for a chat/thread
 * Resolves membership preference → supergroup/legacy overrides → user default
 * @param {number} bubbleId - Chat/thread ID
 * @returns {string|null} 'ALL', 'MENTIONS', 'NONE', or null
 */
export const getNotificationPreference = (bubbleId) => {
	const chat = getChat(bubbleId)
	const membership = getMyMembershipForBubble(bubbleId)
	const userPreference = getMyUser()?.notificationpreference

	let preference = membership?.notificationpreference

	if (chat?.is_supergroup && preference === 'DEFAULT_INHERIT') {
		return 'MENTIONS'
	}

	if (preference === 'MENTIONS_EXCLUDE_ALL') {
		return 'MENTIONS'
	}

	if (preference && preference !== 'DEFAULT_INHERIT') {
		return preference
	}

	// Thread fallback: check parent chat preference before user preference
	if (chat?.is_thread && chat?.parent_chat_id) {
		const parentMembership = getMyMembershipForBubble(chat.parent_chat_id)
		const parentPref = parentMembership?.notificationpreference
		if (parentPref && parentPref !== 'DEFAULT_INHERIT') {
			return parentPref === 'MENTIONS_EXCLUDE_ALL' ? 'MENTIONS' : parentPref
		}
	}

	return userPreference ?? null
}
